import {
  addMonths,
  differenceInMonths,
  endOfMonth,
  parseISO,
  setDate,
  startOfMonth,
  subMonths,
} from 'date-fns';

/**
 * The x domain of the Lifestyle Score chart is a fixed range of 12 months,
 * where the end value is 1 month *forwards* from the given start date
 * (ie, the present).
 */
export const getXScaleDomain = (startDate = new Date()) => {
  const max = setDate(addMonths(startDate, 1), 1);
  // Use a slightly larger value to prevent bars
  // from clipping on the left edge of the chart.
  const min = subMonths(max, 13);
  return [min, max];
};

/**
 * The labels for the x axis of the Lifestyle Score chart should be placed every
 * three months, going backwards from the present month.
 */
export const getXScaleTicks = (startDate = new Date()) => {
  const lastTick = setDate(startDate, 1);
  return new Array(4).fill(Boolean).map((_, i) => subMonths(lastTick, 3 * i));
};

/**
 * When the Lifestyle Score chart is otherwise empty, a "ghost value" is shown
 * aligned with the most recent tick.
 */
export const getFallbackData = (date = new Date()) => [
  {
    registeredDate: setDate(date, 1).toISOString(),
    lifestyleScorePercentage: 100,
  },
];

/**
 * Calculate the x domain based on actual data range
 */
export const getDataDrivenXScaleDomain = (data: any[] = [], fallbackDate = new Date()) => {
  if (data.length === 0) {
    // Fallback to 1-year range if no data
    const max = setDate(addMonths(fallbackDate, 1), 1);
    const min = subMonths(max, 13);
    return [min, max];
  }

  const dates = data.map(d => parseISO(d.registeredDate));
  const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

  // Add some padding
  const min = startOfMonth(subMonths(minDate, 1));
  const max = endOfMonth(addMonths(maxDate, 1));

  return [min, max];
};

/**
 * Calculate dynamic tick intervals based on data range
 */
export const getDataDrivenXScaleTicks = (data: any[] = [], fallbackDate = new Date()) => {
  if (data.length === 0) {
    // Fallback to quarterly ticks for 1 year
    const lastTick = setDate(fallbackDate, 1);
    return new Array(4).fill(Boolean).map((_, i) => subMonths(lastTick, 3 * i));
  }

  const dates = data.map(d => parseISO(d.registeredDate));
  const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

  const totalMonths = differenceInMonths(maxDate, minDate);

  // Determine optimal interval and number of ticks
  let interval: number;
  let maxTicks: number;

  if (totalMonths <= 6) {
    interval = 1; // Monthly
    maxTicks = 6;
  } else if (totalMonths <= 12) {
    interval = 2; // Bi-monthly
    maxTicks = 6;
  } else if (totalMonths <= 24) {
    interval = 3; // Quarterly
    maxTicks = 8;
  } else if (totalMonths <= 48) {
    interval = 6; // Semi-annually
    maxTicks = 8;
  } else {
    interval = 12; // Annually
    maxTicks = 6;
  }

  const numTicks = Math.min(Math.ceil(totalMonths / interval) + 1, maxTicks);
  const latestTick = startOfMonth(maxDate);

  return new Array(numTicks).fill(Boolean).map((_, i) => subMonths(latestTick, interval * i));
};
